// Maps an audit record's raw fields onto the panel's existing repricing labels,
// so the audit trail speaks the same language as the forms it records.

const FIELD_KEYS = {
    reduction_type: 'reprice.reduction_type',
    reduction_value: 'reprice.reduction_value',
    reduction_direction: 'reprice.reduction_direction',
    protection_strategy: 'reprice.protection_strategy',
    is_active: 'reprice.is_active',
    is_auto_update: 'reprice.is_auto_update',
    no_competitor_strategy: 'reprice.no_competitor_strategy',
    ignore_some_competitors: 'reprice.ignore_some_competitors',
    guarantee_buybox: 'reprice.mode_buybox',
    buybox_step: 'reprice.buybox_step',
    buybox_step_type: 'dynamic_pricing_audit.field.buybox_step_type',
    buybox_upward_probe: 'reprice.buybox_upward_probe',
    buybox_up_step: 'reprice.buybox_up_step',
    buybox_up_step_type: 'dynamic_pricing_audit.field.buybox_up_step_type',
    abandon_margin_pct: 'reprice.abandon_margin_pct',
    patience_hours: 'reprice.patience_hours',
    min_value: 'reprice.min_value',
    max_value: 'reprice.max_value',
    optimum_value: 'reprice.optimum_value',
};

// Enum-valued fields whose stored value maps to an existing label.
const VALUE_KEYS = {
    reduction_type: {amount: 'reprice.amount', ratio: 'reprice.ratio'},
    buybox_step_type: {amount: 'reprice.amount', percentage: 'reprice.ratio'},
    buybox_up_step_type: {amount: 'reprice.amount', percentage: 'reprice.ratio'},
    protection_strategy: {
        min_value: 'reprice.suggest_min_value',
        optimum_value: 'reprice.suggest_optimum_value',
        do_nothing: 'reprice.do_nothing',
    },
};

export const fieldLabel = (t, field) => (FIELD_KEYS[field] ? t(FIELD_KEYS[field]) : field);

export const formatValue = (t, field, value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (field === 'is_active') return value ? t('actions.active') : t('actions.passive');
    if (typeof value === 'boolean') return value ? t('yes_no.yes') : t('yes_no.no');
    if (field === 'reduction_direction') return value < 0 ? t('reprice.high') : t('reprice.low');
    const map = VALUE_KEYS[field];
    if (map && map[value]) return t(map[value]);
    return String(value);
};

// notes carries free-form bookkeeping (Excel row counts, competitor toggle
// flags, ...) written by the backend with raw English keys -- label and
// format them through i18next like every other user-facing string here. An
// unknown key still renders (falls back to the raw key) so a future note
// key can't blank the row.
export const noteLabel = (t, key) => t('dynamic_pricing_audit.note.' + key, key);

export const formatNoteValue = (t, value) => {
    if (typeof value === 'boolean') return value ? t('yes_no.yes') : t('yes_no.no');
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
};

export const actionLabel = (t, row) => t('dynamic_pricing_audit.action.' + row.action, row.action);

export const scopeLabel = (t, scope) => t('dynamic_pricing_audit.scope.' + scope, scope);

export const scopeSeverity = (scope) => (
    // 'account' is danger: it is only written by the admin tool that wipes every
    // strategy in the account, which is the entry a user most needs to spot.
    {template: 'info', campaign: 'success', product: 'warning', account: 'danger'}[scope] || null
);

// One-line summary of what changed, e.g. "Reduction value 5 → 7, Status Passive → Active".
// Bulk operations have no per-field diff, so they summarize by affected count.
export const changeSummary = (t, row) => {
    const changes = row.changes || [];
    if (changes.length) {
        return changes
            .map(c => fieldLabel(t, c.field) + ' ' + formatValue(t, c.field, c.before)
                + ' → ' + formatValue(t, c.field, c.after))
            .join(' · ');
    }
    if (row.affected_count > 1) {
        return t('dynamic_pricing_audit.affected', {count: row.affected_count});
    }
    return t('dynamic_pricing_audit.no_field_change');
};
